import React, { useEffect, useRef, useState } from 'react';
import { Stack, Button, TextField, IconButton, Tooltip, CircularProgress, Fade } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { useUnit } from 'effector-react';
import { $tenantDataStartDate } from '../../store/auth';

dayjs.locale('ru');

export default function CorpsDevicesToolbar({
  date,
  onDateChange,
  corpsTabs = [],           // [{id, name, active}]
  onPickCorps,              // (id) => void
  onSearch,                 // (value) => void
  onExport,                 // () => void
  exportDisabled = false,
  exportLoading = false,
  hideExport = false,
  searchValue = '',
}) {
  const tenantDataStartDate = useUnit($tenantDataStartDate);
  const [searchMode, setSearchMode] = useState(false);
  const [searchInput, setSearchInput] = useState(searchValue || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (searchMode) {
      setSearchInput(searchValue || '');
      inputRef.current?.focus();
    }
  }, [searchMode, searchValue]);

  useEffect(() => {
    if (!searchMode) {
      setSearchInput(searchValue || '');
    }
  }, [searchValue, searchMode]);

  const submitSearch = () => {
    const value = searchInput.trim();
    onSearch?.(value);
  };

  const cancelSearch = () => {
    setSearchMode(false);
    setSearchInput('');
    onSearch?.('');
  };

  const controlsVisible = !searchMode;
  const dateValue = date && dayjs(date).isValid() ? dayjs(date) : null;
  const minDate = tenantDataStartDate ? dayjs(tenantDataStartDate) : undefined;

  return (
    <div
      style={{
        background: 'rgba(38, 65, 94, 0.04)',
        border: '1px solid rgba(38, 65, 94, 0.08)',
        borderRadius: 12,
        padding: 12,
        marginBottom: 12,
      }}
    >
      {controlsVisible ? (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <DatePicker
              label="Дата показаний"
              value={dateValue}
              minDate={minDate}
              onChange={(value) => {
                if (!value || !value.isValid?.()) {
                  onDateChange?.(null);
                  return;
                }
                onDateChange?.(value.format('YYYY-MM-DD'));
              }}
              format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 180,
                    background: '#fff',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  },
                },
                openPickerButton: {
                  sx: { color: '#2E73B8' },
                },
                popper: {
                  placement: 'bottom-start',
                },
              }}
              slots={{
                openPickerIcon: CalendarMonthRoundedIcon,
              }}
            />
          </LocalizationProvider>

          {/* переключатели корпусов */}
          <Stack direction="row" spacing={1} sx={{ ml: { sm: 0.5 } }}>
            {corpsTabs.map((t) => (
              <Button
                key={t.id}
                variant={t.active ? 'contained' : 'outlined'}
                onClick={() => onPickCorps?.(t.id)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {t.name}
              </Button>
            ))}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
            <Button
              variant="outlined"
              startIcon={<SearchRoundedIcon />}
              onClick={() => setSearchMode(true)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Поиск
            </Button>
            {!hideExport ? (
              <Tooltip title="Выгрузить в Excel">
                <span>
                  <Button
                    variant="contained"
                    onClick={() => onExport?.()}
                    startIcon={exportLoading ? <CircularProgress size={18} color="inherit" /> : <FileDownloadRoundedIcon />}
                    disabled={exportDisabled || exportLoading}
                    sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none' }}
                  >
                    Выгрузить
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>
      ) : (
        <Fade in>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              fullWidth
              inputRef={inputRef}
              size="small"
              placeholder="Поиск по устройствам, квартирам, серийному номеру..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  submitSearch();
                } else if (e.key === 'Escape') {
                  cancelSearch();
                }
              }}
              sx={{
                background: '#fff',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <Button
              variant="contained"
              onClick={submitSearch}
              sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none' }}
            >
              Найти
            </Button>
            <IconButton onClick={cancelSearch} aria-label="Закрыть поиск">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Fade>
      )}
    </div>
  );
}
